import { computed, ref } from 'vue';

import { FooterComposable, FooterTopStyles } from '@/models/entities/footer.model';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import {
  RequestSelectionBoxesStatus,
  RequestSelectionBoxesStep,
} from '@dh-io-otb/shared/src/models/entities/request-selection-boxes.model';

const useFooterPosition = (): FooterComposable => {
  const { state } = useStore();
  const pendingUpdate = ref(false);
  const footerTopPosition = ref(document.documentElement.clientHeight);

  const footerTopStyle = computed((): FooterTopStyles => {
    // forcing gpu acceleration to avoid flickering - only works inline
    return {
      top: `${footerTopPosition.value}px`,
      transform: 'translate3d(0,0,0)',
    };
  });

  const calculateFooterTopPosition = (): void => {
    if (pendingUpdate.value) return;
    pendingUpdate.value = true;

    requestAnimationFrame(() => {
      pendingUpdate.value = false;

      const filterOptionsButtonHeight = 112;
      const documentClientHeight = document.documentElement.clientHeight;
      const windowInnerHeight = window.innerHeight;
      const windowVisualViewportHeight = window?.visualViewport?.height ?? 0;
      const mobileFloatingBars = Math.max(0, windowInnerHeight - documentClientHeight);

      const owcFooterElement = document.querySelector('owc-footer') as HTMLElement;
      // if footer is not on the page
      if (!owcFooterElement) {
        // position should be bottom of the screen
        footerTopPosition.value = documentClientHeight + mobileFloatingBars;
        // subtract the height of the button to the position
        footerTopPosition.value -= filterOptionsButtonHeight;
        return;
      }

      // owcFooter position relative to viewport
      const owcFooterTopPositionViewport = owcFooterElement.getBoundingClientRect().top;

      const onScreenKeyboardHeight = Math.max(Math.round(documentClientHeight - windowVisualViewportHeight), 0);
      // if virtual keyboard is expanded
      if (onScreenKeyboardHeight > 0) {
        // position should be bottom of the keyboard
        footerTopPosition.value = Math.min(windowInnerHeight, owcFooterTopPositionViewport + onScreenKeyboardHeight);
        // subtract the height of the button to the position
        footerTopPosition.value -= filterOptionsButtonHeight;
        return;
      }

      // if footer is below the bottom of the screen, position should be bottom of the screen
      if (owcFooterTopPositionViewport > windowInnerHeight) footerTopPosition.value = windowInnerHeight;
      // if footer is above the top of the screen, position should be top of the footer
      else footerTopPosition.value = owcFooterTopPositionViewport;

      // subtract the height of the button to the position
      footerTopPosition.value -= filterOptionsButtonHeight;

      // Prevent the footer from going above its minimum position,
      // which is the height of the viewport minus the height of the footer
      // if we are inside the relevant request modal
      if (state.otbcFlow.requestSelectionBoxesStep === RequestSelectionBoxesStep.SLOT) {
        footerTopPosition.value = windowInnerHeight - filterOptionsButtonHeight;
      }
    });
  };
  const footerPositionScrollEvent = (): void => {
    window.addEventListener('scroll', calculateFooterTopPosition);
    window.addEventListener('resize', calculateFooterTopPosition);
    window.visualViewport?.addEventListener('scroll', calculateFooterTopPosition);
    window.visualViewport?.addEventListener('resize', calculateFooterTopPosition);
  };

  return {
    calculateFooterTopPosition,
    footerTopPosition,
    footerPositionScrollEvent,
    footerTopStyle,
  };
};

export { useFooterPosition };
